import React, { useCallback, useEffect, useState } from "react";
import { get, push, ref, runTransaction, set } from "firebase/database";
import {
  ensureAnonymousUser,
  firebaseDatabase,
  isFirebaseSecurityConfigured,
} from "../../firebase/client";
import { type WeddingLanguage, weddingContent } from "./content";

interface Wish {
  id: string;
  name: string;
  message: string;
  date: number | string;
  likes: number;
}

interface WishesProps {
  language: WeddingLanguage;
}

const BLOCKED_TERMS = [
  "anal",
  "affair",
  "asshole",
  "bastard",
  "blowjob",
  "break up",
  "breakup",
  "bitch",
  "cam sung",
  "cheat",
  "cheating",
  "chia tay",
  "cock",
  "dead",
  "death",
  "die",
  "dick",
  "dit me",
  "ditme",
  "divorce",
  "dmm",
  "du ma",
  "du me",
  "dump",
  "duma",
  "fck",
  "fuck",
  "fucking",
  "idiot",
  "kill",
  "leave her",
  "leave him",
  "mistress",
  "moron",
  "murder",
  "porn",
  "pussy",
  "slut",
  "stfu",
  "suicide",
  "vcl",
  "whore",
  "wtf",
  "đen đủi",
  "đéo",
  "đĩ",
  "đĩ điếm",
  "địt",
  "đm",
  "đmm",
  "đụ",
  "cặc",
  "khốn",
  "lồn",
  "ngoại tình",
  "phản bội",
  "ly dị",
  "cắm sừng",
];

const INITIAL_WISHES: Wish[] = [];

const stickyColorClasses = [
  "wish-sticky__item--yellow",
  "wish-sticky__item--pink",
  "wish-sticky__item--blue",
  "wish-sticky__item--green",
  "wish-sticky__item--purple",
  "wish-sticky__item--orange",
  "wish-sticky__item--rose",
  "wish-sticky__item--mint",
  "wish-sticky__item--peach",
];

const shuffleWishes = (items: Wish[]) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const orderWishes = (items: Wish[], preserveRestOrder = true) => {
  const ranked = [...items]
    .filter((wish) => wish.likes > 0)
    .sort((left, right) => right.likes - left.likes)
    .slice(0, 3);
  const rankedIds = new Set(ranked.map((wish) => wish.id));
  const remainingSource = items.filter((wish) => !rankedIds.has(wish.id));
  const remaining = preserveRestOrder
    ? remainingSource
    : shuffleWishes(remainingSource);

  return [...ranked, ...remaining];
};

const normalizeForFilter = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeWishMessage = (value: string) => value.replace(/\r\n?/g, "\n");

const normalizeWishDate = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const numericValue = Number(trimmedValue);

    if (/^\d+$/.test(trimmedValue) && Number.isFinite(numericValue)) {
      return numericValue;
    }

    return trimmedValue;
  }

  return "";
};

const stripUrls = (value: string) =>
  value
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/www\.\S+/gi, " ")
    .replace(
      /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:com|net|org|edu|gov|mil|info|biz|io|co|me|app|dev|ai|gg|tv|vn|us|uk|au|ca|de|jp|kr|sg|xyz)(?:\/\S*)?\b/gi,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();

const normalizeWish = (id: string, wish: any): Wish => ({
  id,
  name: String(wish?.name || ""),
  message: normalizeWishMessage(String(wish?.message || "")),
  date: normalizeWishDate(wish?.date),
  likes: Number.isFinite(Number(wish?.likes)) ? Number(wish.likes) : 0,
});

const formatLikes = (likes: number) => (likes >= 1000 ? "+1k" : `${likes}`);

const formatWishDate = (date: Wish["date"], language: WeddingLanguage) => {
  if (typeof date !== "number" || !Number.isFinite(date)) {
    return date;
  }

  return new Intl.DateTimeFormat(language === "vi" ? "vi-VN" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(date));
};

const containsBlockedTerm = (value: string) => {
  const normalizedValue = ` ${normalizeForFilter(value)} `;

  return BLOCKED_TERMS.some((term) => {
    const normalizedTerm = normalizeForFilter(term);
    return normalizedTerm && normalizedValue.includes(` ${normalizedTerm} `);
  });
};

export function Wishes({ language }: WishesProps) {
  const content = weddingContent[language].wishes;

  const [formData, setFormData] = useState({ name: "", message: "" });
  const [wishes, setWishes] = useState<Wish[]>(INITIAL_WISHES);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [requestError, setRequestError] = useState("");
  const [likeCooldowns, setLikeCooldowns] = useState<Record<string, boolean>>(
    {},
  );
  const [likeBumps, setLikeBumps] = useState<Record<string, boolean>>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const requestWishes = useCallback(async () => {
    try {
      if (!isFirebaseSecurityConfigured || !firebaseDatabase) {
        setRequestError(content.appCheckError);
        return;
      }

      await ensureAnonymousUser();

      const snapshot = await get(ref(firebaseDatabase, "weddingWishes"));
      const wishesRes = snapshot.val();

      if (wishesRes) {
        const newWishes = Object.entries(wishesRes)
          .map(([id, wish]) => normalizeWish(id, wish))
          .filter(Boolean);

        setWishes(() => orderWishes(newWishes, false));
      } else {
        setWishes([]);
      }

      setRequestError("");
    } catch (error) {
      console.error("Error fetching wishes:", error);
      setRequestError(content.loadError);
    }
  }, [content.appCheckError, content.loadError]);

  useEffect(() => {
    requestWishes();
  }, [requestWishes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.name.trim() && formData.message.trim()) {
      const sanitizedName = stripUrls(formData.name);
      const sanitizedMessage = normalizeWishMessage(stripUrls(formData.message));

      if (!sanitizedName || !sanitizedMessage) {
        setFormData({ name: "", message: "" });
        return;
      }

      if (
        containsBlockedTerm(sanitizedName) ||
        containsBlockedTerm(sanitizedMessage)
      ) {
        setFormData({ name: "", message: "" });
        return;
      }

      const newWish: Wish = {
        id: "",
        name: sanitizedName,
        message: sanitizedMessage,
        date: Date.now(),
        likes: 0,
      };

      try {
        setLoading(true);
        setRequestError("");

        if (!isFirebaseSecurityConfigured || !firebaseDatabase) {
          throw new Error("Firebase security is not configured.");
        }

        await ensureAnonymousUser();

        const wishRef = push(ref(firebaseDatabase, "weddingWishes"));
        const createdWish = {
          ...newWish,
          id: wishRef.key || "",
        };
        await set(wishRef, createdWish);

        setWishes((prev) => [createdWish, ...prev]);
        setFormData({ name: "", message: "" });
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
      } catch (_error) {
        setRequestError(content.sendError);
      } finally {
        setLoading(false);
      }
    }
  };

  const filteredWishes = wishes.filter((wish) => {
    const normalizedQuery = normalizeForFilter(searchTerm);

    if (!normalizedQuery) {
      return true;
    }

    return (
      normalizeForFilter(wish.name).includes(normalizedQuery) ||
      normalizeForFilter(wish.message).includes(normalizedQuery)
    );
  });

  const topWishRanks = wishes
    .filter((wish) => wish.likes > 0)
    .sort((left, right) => right.likes - left.likes)
    .slice(0, 3)
    .reduce<Record<string, number>>((accumulator, wish, index) => {
      accumulator[wish.id] = index + 1;
      return accumulator;
    }, {});

  const handleLike = async (wishId: string) => {
    if (
      !isFirebaseSecurityConfigured ||
      !firebaseDatabase ||
      !wishId ||
      likeCooldowns[wishId]
    ) {
      return;
    }

    setLikeCooldowns((prev) => ({ ...prev, [wishId]: true }));
    setLikeBumps((prev) => ({ ...prev, [wishId]: true }));

    setWishes((prev) =>
      prev.map((wish) =>
        wish.id === wishId ? { ...wish, likes: wish.likes + 1 } : wish,
      ),
    );

    try {
      await ensureAnonymousUser();
        await runTransaction(
          ref(firebaseDatabase, `weddingWishes/${wishId}/likes`),
          (currentLikes) => {
          const safeLikes =
            Number.isFinite(Number(currentLikes)) && currentLikes !== null
              ? Number(currentLikes)
              : 0;
          return safeLikes + 1;
        },
      );
      setWishes((prev) => orderWishes(prev, true));
    } catch (_error) {
      setWishes((prev) =>
        prev.map((wish) =>
          wish.id === wishId
            ? { ...wish, likes: Math.max(0, wish.likes - 1) }
            : wish,
        ),
      );
    } finally {
      window.setTimeout(() => {
        setLikeCooldowns((prev) => {
          const next = { ...prev };
          delete next[wishId];
          return next;
        });
      }, 1200);
      window.setTimeout(() => {
        setLikeBumps((prev) => {
          const next = { ...prev };
          delete next[wishId];
          return next;
        });
      }, 380);
    }
  };

  return (
    <section id="wishes" className="wedding-section wedding-section--white">
      <div className="wedding-container">
        <div className="wedding-header">
          <h2 className="wedding-title">{content.heading}</h2>
          <div className="wedding-divider" />
        </div>

        <div className="wishes-grid">
          <div className="wishes-form">
            <form onSubmit={handleSubmit} className="wishes-form__card">
              <h3 className="wishes-form__title">{content.formTitle}</h3>

              <div className="wishes-fields">
                <div className="wishes-field">
                  <label htmlFor="wish-name">{content.nameLabel}</label>
                  <input
                    id="wish-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={content.namePlaceholder}
                    className="wishes-input"
                    required
                  />
                </div>

                <div className="wishes-field">
                  <label htmlFor="wish-message">{content.messageLabel}</label>
                  <textarea
                    id="wish-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={content.messagePlaceholder}
                    rows={5}
                    className="wishes-textarea"
                    required
                  />
                </div>
              </div>

              <button type="submit" className="wedding-button wishes-submit">
                {loading ? content.submitLoading : content.submitIdle}
              </button>

              {submitted && (
                <div className="wishes-success">{content.success}</div>
              )}
            </form>
          </div>

          <div className="wishes-display">
            <div className="wishes-toolbar">
              <div className="wishes-toolbar__search">
                <div className="wishes-toolbar__label-row">
                  <label htmlFor="wish-search" className="wishes-toolbar__label">
                    {content.searchLabel}
                  </label>
                  <span className="wishes-toolbar__count">
                    {wishes.length === 1
                      ? "1 lovely wish"
                      : `${wishes.length} lovely wishes`}
                  </span>
                </div>
                <input
                  id="wish-search"
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={content.searchPlaceholder}
                  className="wishes-input"
                />
              </div>
            </div>

            <div className="wishes-display__surface">
              {requestError && <p className="wishes-error">{requestError}</p>}
              {!filteredWishes.length && (
                <p className="wishes-empty">{content.empty}</p>
              )}
              <WishStickyMode
                language={language}
                wishes={filteredWishes}
                onLike={handleLike}
                likeCooldowns={likeCooldowns}
                likeActionLabel={content.likeAction}
                likeBumps={likeBumps}
                topWishRanks={topWishRanks}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WishStickyMode({
  language,
  wishes,
  onLike,
  likeCooldowns,
  likeActionLabel,
  likeBumps,
  topWishRanks,
}: {
  language: WeddingLanguage;
  wishes: Wish[];
  onLike: (wishId: string) => void;
  likeCooldowns: Record<string, boolean>;
  likeActionLabel: string;
  likeBumps: Record<string, boolean>;
  topWishRanks: Record<string, number>;
}) {
  return (
    <div className="wish-sticky">
      {wishes.map((wish, index) => {
        const rotation = ((index % 5) - 2) * 2;
        const colorClass =
          stickyColorClasses[index % stickyColorClasses.length];
        const topRank = topWishRanks[wish.id];

        return (
          <div
            key={wish.id || index}
            className={`wish-sticky__item ${colorClass}`}
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <div className="wish-sticky__tape" />
            {topRank ? (
              <div
                className={`wish-rank wish-rank--${topRank}`}
                aria-label={language === "vi" ? `Lời chúc được yêu thích thứ ${topRank}` : `Top ${topRank} most liked wish`}
                title={language === "vi" ? `Lời chúc được yêu thích thứ ${topRank}` : `Top ${topRank} most liked wish`}
              >
                <span className="wish-rank__crown" aria-hidden="true">
                  ♛
                </span>
                {/* <span className="wish-rank__label">{topRank}</span> */}
              </div>
            ) : null}

            <div className="wish-sticky__body">
              <p className="wish-common__name">{wish.name}</p>
              <p className="wish-common__message">{wish.message}</p>
              <p className="wish-common__date">
                {formatWishDate(wish.date, language)}
              </p>
              <button
                type="button"
                className={`wish-like ${
                  likeBumps[wish.id] ? "wish-like--bump" : ""
                }`}
                onClick={() => onLike(wish.id)}
                disabled={Boolean(likeCooldowns[wish.id])}
                aria-label={language === "vi" ? `${likeActionLabel} lời chúc của ${wish.name}` : `${likeActionLabel} wish from ${wish.name}`}
                title={likeActionLabel}
              >
                <span className="wish-like__heart" aria-hidden="true">
                  ♥
                </span>
                {/* <span className="wish-like__text">{likeActionLabel}</span> */}
                <span className="wish-like__count">{formatLikes(wish.likes)}</span>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
